/**
 * "Our approach in focus" — alternating image/text feature rows that give the long-scroll
 * page weight and tell the why-it's-different story with dignified imagery and proof points.
 */
import { Fingerprint, ShieldCheck, ReceiptText, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button.jsx';
import { Figure } from '../components/ui/Figure.jsx';
import { useReveal } from '../hooks/useReveal.js';
import { MEDIA } from '../data/media.js';
import styles from './ApproachBand.module.css';

const ROWS = [
  {
    img: MEDIA.planning,
    eyebrow: 'Verified, not vouched',
    title: 'Every case starts with evidence on the ground.',
    body: 'Staff record each case offline, with documents. A field agent then captures GPS-tagged before/after photos with the timestamp baked in. Nothing reaches the wall on someone’s word — it reaches the wall on proof.',
    points: [
      { icon: Fingerprint, text: 'Offline, staff-only intake — no public form to flood or game.' },
      { icon: ShieldCheck, text: 'GPS + timestamped field photos verify the work physically happened.' },
    ],
  },
  {
    img: MEDIA.ledger,
    eyebrow: 'Every rupee, accounted for',
    title: 'The money trail is as auditable as the help.',
    body: 'Donations land in a dedicated account and are disbursed against verified cases — often paid vendor-direct to a ration shop or hospital, so value can’t leak. The transparency report totals it all, live, from the same database.',
    points: [
      { icon: ReceiptText, text: 'Vendor-direct disbursement keeps cash out of the chain where it can.' },
      { icon: CheckCircle2, text: 'A hash-chained audit trail makes the ledger tamper-evident.' },
    ],
    flip: true,
  },
];

export function ApproachBand() {
  return (
    <section className={styles.section}>
      <div className="container">
        <div className={styles.intro}>
          <p className="eyebrow">Our approach</p>
          <h2 className={styles.title}>Built so a sceptic can check the work.</h2>
          <p className={styles.lede}>
            Most welfare asks you to trust a photo and a promise. We invert it: the platform
            assumes you won’t take our word for anything — and gives you the receipts.
          </p>
        </div>

        {ROWS.map((row) => (
          <Row key={row.title} {...row} />
        ))}

        <div className={styles.cta}>
          <Button to="/transparency" variant="primary">
            Read the transparency report
          </Button>
          <Button to="/about" variant="outline">
            How we’re governed
          </Button>
        </div>
      </div>
    </section>
  );
}

function Row({ img, eyebrow, title, body, points, flip }) {
  const ref = useReveal({ threshold: 0.2 });
  return (
    <div ref={ref} className={`${styles.row} ${flip ? styles.flip : ''}`} data-revealed="false">
      <div className={styles.copy}>
        <p className={styles.rowEyebrow}>{eyebrow}</p>
        <h3 className={styles.rowTitle}>{title}</h3>
        <p className={styles.rowBody}>{body}</p>
        <ul className={styles.points}>
          {points.map((p) => (
            <li key={p.text}>
              <span className={styles.pIcon}>
                <p.icon size={18} aria-hidden="true" />
              </span>
              <span>{p.text}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.figureWrap}>
        <Figure id={img} alt={title} ratio="4 / 3" w={760} />
      </div>
    </div>
  );
}

export default ApproachBand;
